import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SAMPLE_AVATARS, type HumanFilter, type HumanScope } from "./data";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </svg>
);

export const AiDigitalHumanAvatars: FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<HumanFilter>("all");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [cloneMode, setCloneMode] = useState<"photo" | "video">("photo");
  const [cloneConsent, setCloneConsent] = useState(false);
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2600);
  };

  const filtered = useMemo(() => {
    const base = filter === "all" ? SAMPLE_AVATARS : SAMPLE_AVATARS.filter((a) => a.scope === filter);
    return base.filter((a) => a.name.includes(search));
  }, [filter, search]);

  const scopeLabel = (scope: HumanScope) => (scope === "public" ? "公共形象" : "我的形象");

  return (
    <div className="adh-page">
      <header className="adh-page-header">
        <div>
          <span className="adh-eyebrow">Avatar Center</span>
          <h1>我的数字人</h1>
          <p>查看公共形象，管理我的形象。</p>
        </div>
        <div className="adh-page-actions">
          <button className="adh-text-btn" type="button" onClick={() => navigate("/hub/ai-digital-human")}>返回创作</button>
          <button className="adh-primary-btn" type="button" onClick={() => setModal(true)}>
            <PlusIcon /> 克隆新形象
          </button>
        </div>
      </header>

      <div className="adh-toolbar">
        <div className="adh-filter-tabs">
          <button className={filter === "all" ? "active" : ""} type="button" onClick={() => setFilter("all")}>全部</button>
          <button className={filter === "public" ? "active" : ""} type="button" onClick={() => setFilter("public")}>公共形象</button>
          <button className={filter === "mine" ? "active" : ""} type="button" onClick={() => setFilter("mine")}>我的形象</button>
        </div>
        <label className="adh-search">
          <SearchIcon />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索数字人名称" />
        </label>
      </div>

      <div className="adh-avatar-grid">
        {filtered.map((a) => (
          <article key={a.id} className="adh-avatar-card">
            <div className="adh-avatar-thumb">
              <span className="adh-scope-tag">{scopeLabel(a.scope)}</span>
              <div className="adh-avatar-figure" style={{ background: a.color }}>
                <span className="adh-avatar-head" />
              </div>
            </div>
            <div className="adh-avatar-meta">
              <strong>{a.name}</strong>
              <small>{a.tone}</small>
            </div>
          </article>
        ))}
      </div>

      {modal && (
        <div className="adh-modal-backdrop" onClick={() => setModal(false)}>
          <div className="adh-modal adh-modal--md" onClick={(e) => e.stopPropagation()}>
            <header>
              <span className="adh-eyebrow">Avatar Clone</span>
              <h3>克隆新形象</h3>
              <p>请确保已获得出镜人的明确授权。</p>
              <button type="button" className="adh-modal-close" onClick={() => setModal(false)}>×</button>
            </header>
            <div className="adh-modal-body">
              <div className="adh-clone-tabs">
                <button className={cloneMode === "photo" ? "active" : ""} type="button" onClick={() => setCloneMode("photo")}>照片创建</button>
                <button className={cloneMode === "video" ? "active" : ""} type="button" onClick={() => setCloneMode("video")}>视频克隆</button>
              </div>
              <label className="adh-form-field">
                <span>数字人名称 <b>*</b></span>
                <input placeholder="例如：企业讲师小林" />
              </label>
              <button className="adh-upload-zone" type="button">
                <UploadIcon />
                <strong>{cloneMode === "photo" ? "上传正面清晰的半身照片" : "上传一段清晰讲话视频"}</strong>
                <small>{cloneMode === "photo" ? "JPG、PNG · 仅一人出镜 · 面部无遮挡" : "MP4 · 30s–120s · 正脸讲话"}</small>
              </button>
              <label className="adh-checkbox">
                <input type="checkbox" checked={cloneConsent} onChange={(e) => setCloneConsent(e.target.checked)} />
                <span>我确认已获得出镜人对数字人形象制作和使用的明确授权。</span>
              </label>
            </div>
            <footer>
              <span>提交后开始克隆，完成后才可使用</span>
              <button className="adh-primary-btn" type="button" disabled={!cloneConsent} onClick={() => { setModal(false); showToast("已提交克隆任务"); }}>
                提交克隆任务
              </button>
            </footer>
          </div>
        </div>
      )}

      {toast && <div className="adh-toast" role="status">{toast}</div>}
    </div>
  );
};
